/**
 * Unit type definitions with base stats and traits
 *
 * Units are loaded from JSON configuration (units.json) and converted
 * to the UnitDefinition format used by the game systems.
 */

import { loadAndValidateUnits } from '@config/UnitConfigLoader';
import unitsJson from '@config/units.json';

export interface UnitDefinition {
  unitType: string;
  name: string;
  baseStats: {
    attack: number;
    defense: number;
    speed: number;
    abilityPower: number;
    maxHealth: number;
    attackRange: number;
    attackSpeed: number;
  };
  traits: string[];
  tier: number; // 1-5, affects shop rarity
  meshType: UnitMeshType;
  color: number;
  model?: string; // Path to GLB model file
  scale?: number; // Model scale multiplier
}

export enum UnitMeshType {
  Soldier = 'soldier', // Cube-based humanoid
  Beast = 'beast', // Four-legged creature
  Mage = 'mage', // Robed figure with staff
  Assassin = 'assassin', // Slim, agile figure
  Golem = 'golem', // Large, bulky figure
  Archer = 'archer', // Ranged unit
}

/**
 * Star level stat multipliers
 */
export const STAR_MULTIPLIERS: Record<number, number> = {
  1: 1.0,
  2: 1.5,
  3: 2.25,
  4: 3.5,
};

/**
 * All unit definitions - loaded from JSON config
 */
export const UNIT_DEFINITIONS: Record<string, UnitDefinition> = loadAndValidateUnits(unitsJson);

/**
 * Get a unit definition by type
 */
export function getUnitDefinition(unitType: string): UnitDefinition | undefined {
  return UNIT_DEFINITIONS[unitType];
}

/**
 * Get all unit types
 */
export function getAllUnitTypes(): string[] {
  return Object.keys(UNIT_DEFINITIONS);
}

/**
 * Get units by tier
 */
export function getUnitsByTier(tier: number): UnitDefinition[] {
  return Object.values(UNIT_DEFINITIONS).filter((unit) => unit.tier === tier);
}

/**
 * Get units by trait
 */
export function getUnitsByTrait(trait: string): UnitDefinition[] {
  return Object.values(UNIT_DEFINITIONS).filter((unit) =>
    unit.traits.includes(trait)
  );
}
